import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Camera, Upload, X, RotateCcw, Check, FileText } from 'lucide-react';

export default function DocumentUpload({ 
  docType,
  docName,
  docDescription,
  status,
  rejectionReason,
  isUploading,
  onUpload,
  acceptTypes = 'image/*,.pdf',
  icon: DocIcon = FileText
}) {
  // If document is approved, don't allow replacement
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const [showDialog, setShowDialog] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stream, setStream] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: docType === 'selfie' ? 'user' : 'environment',
          width: { ideal: 1280 }, 
          height: { ideal: 960 } 
        }
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera access denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Failed to access camera. Please try uploading a file instead.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Mirror only for selfie
    if (docType === 'selfie') {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageData);
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }

    // For images, show preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      // For PDFs, upload directly
      uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      await onUpload(file, docType);
      toast.success(`${docName} uploaded successfully!`);
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const uploadCapturedImage = async () => {
    if (!capturedImage) return;
    
    setUploading(true);
    try {
      // Convert base64 to blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const file = new File([blob], `${docType}.jpg`, { type: 'image/jpeg' });
      
      await onUpload(file, docType);
      toast.success(`${docName} uploaded successfully!`);
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setShowDialog(false);
  };

  const getStatusColor = () => {
    if (status === 'approved') return 'bg-green-100 text-green-600';
    if (status === 'pending') return 'bg-amber-100 text-amber-600';
    if (status === 'rejected') return 'bg-red-100 text-red-600';
    return 'bg-slate-100 text-slate-500';
  };

  const getStatusText = () => {
    if (!status) return null;
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <>
      {/* Document Row */}
      <div className="flex flex-col p-3 bg-stone-50 rounded-xl" data-testid={`doc-upload-${docType}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor().split(' ')[0]}`}>
              <DocIcon className={`w-5 h-5 ${getStatusColor().split(' ')[1]}`} />
            </div>
            <div>
              <p className="font-medium text-slate-900">
                {docName}
                <span className="text-red-500 ml-1">*</span>
              </p>
              <p className="text-xs text-slate-500">{docDescription}</p>
              {status && (
                <span className={`text-xs font-medium ${status === 'approved' ? 'text-green-600' : status === 'pending' ? 'text-amber-600' : 'text-red-600'}`}>
                  {getStatusText()}
                </span>
              )}
            </div>
          </div>
          {/* Show button based on status */}
          {isApproved ? (
            <span className="px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded-lg flex items-center gap-1">
              <Check className="w-3.5 h-3.5" />
              Verified
            </span>
          ) : (
            <Button 
              variant={status ? 'outline' : 'default'} 
              size="sm" 
              className={!status ? 'bg-slate-900' : ''} 
              disabled={isUploading}
              onClick={() => setShowDialog(true)}
              data-testid={`upload-btn-${docType}`}
            >
              {isUploading ? 'Uploading...' : status ? 'Replace' : <><Upload className="w-4 h-4 mr-1" /> Upload</>}
            </Button>
          )}
        </div>
        
        {/* Rejection reason display */}
        {isRejected && rejectionReason && (
          <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg">
            <p className="text-xs text-red-700">
              <span className="font-medium">Reason:</span> {rejectionReason}
            </p>
            <p className="text-xs text-red-600 mt-1">Please upload a new document that addresses this issue.</p>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showDialog} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload {docName}</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {/* Camera View */}
            {showCamera && !capturedImage && (
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full rounded-xl bg-black"
                  style={{ transform: docType === 'selfie' ? 'scaleX(-1)' : 'none' }}
                />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-12 h-12 rounded-full bg-white/90 hover:bg-white"
                    onClick={stopCamera}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  <Button
                    size="icon"
                    className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600"
                    onClick={capturePhoto}
                    data-testid={`capture-${docType}-btn`}
                  >
                    <Camera className="w-8 h-8 text-white" />
                  </Button>
                </div>
                {docType === 'selfie' && (
                  <p className="absolute top-4 left-0 right-0 text-center text-white text-sm bg-black/50 py-1">
                    Position your face in the frame
                  </p>
                )}
                {docType === 'id' && (
                  <p className="absolute top-4 left-0 right-0 text-center text-white text-sm bg-black/50 py-1">
                    Position your ID clearly in the frame
                  </p>
                )}
              </div>
            )}

            {/* Captured/Selected Image Preview */}
            {capturedImage && (
              <div className="space-y-4">
                <div className="relative">
                  <img 
                    src={capturedImage} 
                    alt="Preview" 
                    className="w-full rounded-xl"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={retakePhoto}
                    disabled={uploading}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retake
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={uploadCapturedImage}
                    disabled={uploading}
                    data-testid={`confirm-${docType}-btn`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Options (when not in camera mode and no captured image) */}
            {!showCamera && !capturedImage && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 text-center">{docDescription}</p>
                
                <div className="flex flex-col gap-3">
                  <Button
                    className="w-full h-14 bg-slate-900 hover:bg-slate-800"
                    onClick={startCamera}
                    data-testid={`camera-btn-${docType}`}
                  >
                    <Camera className="w-5 h-5 mr-3" />
                    {docType === 'selfie' ? 'Take a Selfie' : 'Take a Photo'}
                  </Button>
                  
                  <div className="relative">
                    <Button
                      variant="outline"
                      className="w-full h-14"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid={`file-btn-${docType}`}
                    >
                      <Upload className="w-5 h-5 mr-3" />
                      Upload from Gallery
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={acceptTypes}
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                </div>

                <p className="text-xs text-center text-slate-500">
                  Supported: JPG, PNG, PDF. Max 10MB.
                </p>
              </div>
            )}

            {/* Hidden canvas for capturing */}
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
